import { useCallback, useEffect, useMemo } from "react";

import { useMutation } from "@apollo/client";
import { FormikProvider, useFormik } from "formik";
import { omit } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AutoSave from "pages/profile/AutoSave";

import {
  buttons,
  dependentButtons,
  getParentingButtons,
  getSupportedByButtons,
  relationshipButtons,
  supportTypeButtons,
} from "components/calculations/components/ChildrenForm/childrenMock";
import Button from "components/common/Button";
import GenderSelect from "components/common/GenderSelect";
import ToggleButtons from "components/common/ToggleButtons";
import DayPickerInput from "components/common/inputs/DayPickerInput";
import TextInput from "components/common/inputs/TextInput";

import assignObjectValuesToSet from "utils/assignObjectValuesToSet";
import { getDateDiff } from "utils/date";

import UPDATE_CLIENT from "@gql/mutations/clients/updateClient";

const ChildForm = ({ user, child, onDeleted }) => {
  const navigate = useNavigate();

  const [updateClient, { loading }] = useMutation(UPDATE_CLIENT);

  const initialValues = useMemo(
    () => ({
      ...omit(child, ["__typename", "id", "createdAt", "updatedAt", "lawyer"]),
      firstName: child.firstName || "",
      lastName: child.lastName || "",
      middleName: child.middleName || "",
      birthDate: child.birthDate ? new Date(child.birthDate) : null,
      startSchoolDate: child.startSchoolDate ? new Date(child.startSchoolDate) : null,
      endSchoolDate: child.endSchoolDate ? new Date(child.endSchoolDate) : null,
    }),
    [child],
  );

  const handleDeleteChild = async () => {
    await updateClient({
      variables: {
        where: { id: user.client.id },
        data: {
          children: {
            delete: {
              id: child.id,
            },
          },
        },
      },
      update: (cache) => {
        cache.evict({ id: `Child:${child.id}` });
      },
    });

    if (typeof onDeleted === "function") {
      onDeleted();
    } else {
      navigate(`/clients/${user.client.id}/edit/children`);
    }
  };

  const onSubmit = useCallback(
    (values) => {
      const variables = {
        where: { id: user.client.id },
        data: {
          children: {
            update: {
              where: { id: child.id },
              data: assignObjectValuesToSet(values),
            },
          },
        },
      };

      if (child?.lawyer?.id && (!values.isParty || child?.isOurClient !== values?.isOurClient)) {
        variables.data.children.update.data.lawyer = {
          delete: {
            id: { equals: child?.lawyer?.id },
          },
        };
      }

      updateClient({ variables })
        .then(() => toast.success("Child data auto-saved"))
        .catch((error) => error.graphQLErrors.map(({ message }) => toast.error(message)));
    },
    [user.client.id, child.id, updateClient, child?.lawyer?.id, child?.isOurClient],
  );

  const formik = useFormik({ initialValues, onSubmit, enableReinitialize: true });

  const { setFieldValue, values } = formik;

  const { isOfRelationship, isDependent, birthDate, supportType, isParty } = values;

  const age = useMemo(() => {
    return getDateDiff({
      utc: true,
      diffDate: birthDate,
      unit: "years",
    });
  }, [birthDate]);

  const { clientLabel, exLabel } = user.client;
  const { relationship } = user.client;

  const { isSeparated } = relationship || {};

  useEffect(() => {
    if (!isParty) {
      setFieldValue("isOurClient", false);
    }
  }, [isParty, setFieldValue]);

  return (
    <div className="child-form">
      <FormikProvider value={formik}>
        <AutoSave />

        <Form className="update-form-container" name="form">
          <Row className="row">
            <Col md={3} className="pr-md-2">
              <TextInput name="firstName" label="First name" />
            </Col>
            <Col md={3} className="pl-md-2">
              <TextInput name="middleName" label="Middle name(s)" />
            </Col>
            <Col md={3} className="pr-md-2">
              <TextInput name="lastName" label="Last name(s)" />
            </Col>
            <Col md={3} className="pl-md-2">
              <GenderSelect label="Gender" name="gender" />
            </Col>
          </Row>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Group>
                <DayPickerInput
                  name="birthDate"
                  label="Date of Birth"
                  hint={`Enter Child's date of birth.`}
                  touchable
                  onChange={(date) => setFieldValue(`birthDate`, date)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="computed-date">
                <label className="form-control-label">Age</label>
                {birthDate ? (
                  <p className="mt-3">
                    This child is
                    <strong>{` ${age} years `}</strong>
                    old.
                  </p>
                ) : (
                  <p className="mt-3">Select a date of birth</p>
                )}
                <small className="form-text text-muted mt-1">
                  <span>Edit Child&apos;s date of birth if this is wrong.</span>
                </small>
              </Form.Group>
            </Col>
          </Row>
          <Row className="align-items-start">
            <Col md={3}>
              <ToggleButtons
                label="Child of Relationship?"
                name="isOfRelationship"
                buttons={relationshipButtons}
                hint="Is Child a child of this relationship?"
              />
            </Col>
            {age >= 18 && (
              <Col md={3}>
                <ToggleButtons
                  label="Adult Child Dependent?"
                  name="isDependent"
                  buttons={buttons}
                  hint="The child is over the age of 18 and a legal dependent?"
                />
              </Col>
            )}
            <Col md={6}>
              {isOfRelationship ? (
                <ToggleButtons
                  label="Parenting"
                  name="parenting"
                  buttons={getParentingButtons({ clientLabel, exLabel })}
                  hint="Select the primary parent or shared parenting."
                />
              ) : (
                <ToggleButtons
                  label="Dependent Child?"
                  name="isDependent"
                  buttons={dependentButtons}
                  hint="Is Child a legal dependent? This can be complicated if the child is over 18."
                />
              )}
            </Col>
          </Row>
          {isDependent && !values?.isOfRelationship && (
            <Row className="align-items-start">
              <Col md={6}>
                <ToggleButtons
                  label="Child of Prior Relationship?"
                  name="priorRelationship"
                  buttons={buttons}
                  hint="Is the child from a prior relationship? If so, the calculator will apply the 'first family principle' and deduct child support for this child from guideline income for both spousal and child support."
                />
              </Col>
              <Col md={6}>
                <ToggleButtons
                  label="Child is Supported By"
                  name="supportedBy"
                  buttons={getSupportedByButtons({
                    isSeparated,
                    exLabel,
                    clientLabel,
                    isProfessional: true,
                  })}
                  hint="Which person has an obligation to support this child? Since this child is a legal dependent from another relationship, the cost of supporting the child will need to be calculated and deducted from their guideline income for this support calculation."
                />
              </Col>
            </Row>
          )}
          {isDependent && !isOfRelationship && (
            <Row className="align-items-start">
              <Col md={6}>
                <ToggleButtons
                  label="Support Type"
                  className="support-type-input"
                  name="supportType"
                  buttons={supportTypeButtons}
                  hint="Select guideline support to automatically calculate and deduct guideline child support for this child from the parent's guideline income. Alternatively, you can deduct actual section 7 support paid or other support in a specified amount."
                />
              </Col>
              <Col md={6}>
                {supportType === "OTHER" ? (
                  <ToggleButtons
                    label="Tax Deductible?"
                    name="supportDeductible"
                    buttons={buttons}
                    hint="Is this support amount tax deductible? Usually it is not, however if the only support is section 7 expenses then they may be deductible. If support is not deductible, then the actual amount of support will be grossed-up and deducted from guideline income."
                  />
                ) : (
                  <TextInput
                    label="Child's Income"
                    hint="*Note that the child's income is not relevant to the calculation of basic section 3 child support,
                      but is relevant to the calculation of certain tax credits and benefits that can affect section 7 special expense support."
                    name="childIncome"
                    placeholder={0}
                    type="number"
                    prepend="dollar"
                  />
                )}
              </Col>
            </Row>
          )}
          {isDependent && !isOfRelationship && supportType === "OTHER" && (
            <Row className="align-items-start">
              <Col md={6}>
                <TextInput
                  label="Support Amount"
                  hint="*Note that the child's income is not relevant to the calculation of basic section 3 child support,
                            but is relevant to the calculation of certain tax credits and benefits that can affect section 7 special expense support."
                  name="supportAmount"
                  placeholder={0}
                  type="number"
                  prepend="dollar"
                />
              </Col>
              <Col md={6}>
                <TextInput
                  label="Child's Income"
                  hint="*Note that the child's income is not relevant to the calculation of basic section 3 child support,
                    but is relevant to the calculation of certain tax credits and benefits that can affect section 7 special expense support."
                  name="childIncome"
                  placeholder={0}
                  type="number"
                  prepend="dollar"
                />
              </Col>
            </Row>
          )}
          <Row className="align-items-start">
            <Col md={4}>
              <Form.Group>
                <DayPickerInput
                  name="startSchoolDate"
                  label="Date Child Starts School"
                  hint="Enter the date this child starts full-time school. Default is September 1 after the child turns 7."
                  touchable
                  onChange={(date) => setFieldValue(`startSchoolDate`, date)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <DayPickerInput
                  name="endSchoolDate"
                  label="Date Child Finishes School"
                  hint="Enter the date this child finishes high school. Default is June 30 after the child turns 18."
                  touchable
                  onChange={(date) => setFieldValue(`endSchoolDate`, date)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <TextInput
                label="School"
                name="school"
                hint="Enter the name of the child's school."
              />
            </Col>
          </Row>
          {isOfRelationship && (
            <Row>
              <Col md={6}>
                <TextInput
                  label="Child's Income"
                  hint="*Note that the child's income is not relevant to the calculation of basic section 3 child support,
                      but is relevant to the calculation of certain tax credits and benefits that can affect section 7 special expense support."
                  name="childIncome"
                  placeholder={0}
                  type="number"
                  prepend="dollar"
                />
              </Col>
              <Col md={6}>
                <ToggleButtons
                  label="Child Has Disability"
                  name="disabled"
                  buttons={buttons}
                  hint="Is the child eligible for the Disability Tax Credit or other recognized disability benefits?"
                />
              </Col>
            </Row>
          )}
          <Row className="align-items-end">
            <Col md={4}>
              <ToggleButtons
                label="Is Represented by Counsel?"
                name="isParty"
                buttons={buttons}
                hint="Is this child a party to a court action and represented by legal counsel? Answer yes to select a lawyer and access other party-specific settings."
              />
            </Col>
            <Col md={4} className="align-self-start">
              {isParty && (
                <ToggleButtons
                  label="Is Our Client?"
                  name="isOurClient"
                  buttons={buttons}
                  hint="Is this party our client? Answer yes to select lawyers from our organization to display in court documents."
                />
              )}
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              <Button
                onClick={handleDeleteChild}
                disabled={loading}
                variant="danger"
                className="mb-3"
              >
                Remove Child
              </Button>
            </Col>
          </Row>
        </Form>
      </FormikProvider>
    </div>
  );
};

export default ChildForm;
